<template>
	<div
		class="room"
		:class="[
			'room--'+roomType,
			{'room--joined': inRoom},
			{'room--visible': visible},
			{'room--viewing': viewingRoom},
			{'room--in-search': inSearch},
		]"
		:style="style"
	>
		<img v-if="isCreate" src="@/assets/conferencing-icons/create-room.svg"/>
		<div class="room-content" v-else>
			<div v-if="isHub" class="room-content-hub">
				<div v-if="logo" class="room-content-hub__logo" :style="`background-image: url(${logo})`"/>
				<div v-else class="room-content-hub__intials">{{areaInitials}}</div>
			</div>
			<div class="room-content__name">{{nameCapped}}</div>
			<div class="room-content__button" v-if="viewingRoom && !isHub">
				<img :src="require(`@/assets/${buttonContent.icon}`)"/>
				{{buttonContent.text}}
			</div>
			<div class="room-content__online">{{online}}</div>
		</div>
		<transition-group>
			<UserIcon
				class="room__user"
				v-for="({userId, status}, index) in usersToShow"
				:key="userId"
				:userId="userId"
				:userStatus="status"
				:style="getUserPosition(index)"
			/>
		</transition-group>
	</div>
</template>

<script>
/*
----- IMPORTANT INFORMATION -----

This component is designed to be used anytime we need to simulate or preview how rooms will look with dummy data.

This component is NOT for use as a regular room - please use Room.vue for that.

This component should be kept up-to-date to support any new features of Room.vue, and it should have the same behavior where applicable.

*/
import { mapGetters } from 'vuex';

import { processMediaURL, colorIsLight, initaliseAreaName } from '@/functions/utility.js';
import { capRoomNamesToFit } from '@/functions/app.js';

import UserIcon from '../UserIcon.vue';

const TAU = (Math.PI * 2);
const linkButtonContent = {text: 'Explore', icon: '/global-icons/eye-show-small.svg'}
const buttonContentMap = {
  spacein: {
    text: 'Join',
    icon: 'notifications-icon/camcorder-white.svg'
  },
  livestream: {
    text: 'Watch',
    icon: 'conferencing-icons/play.svg'
  },
	portal: {
    text: 'Jump',
    icon: 'global-icons/jump-space.svg'
  },
  link: linkButtonContent,
  internal: linkButtonContent,
  // legacy, probably deprecated
  networking: linkButtonContent,
  unknown: linkButtonContent,
}

export default {
	components: { UserIcon },
	data: () => ({
		mobileNameLimit: 14,
		randomRadians: Math.random() * TAU
	}),
	props: {
		visible: {type: Boolean, default: true},
		name: String,
		inSearch: Boolean,
		areaTitle: String, // only used when roomType is hub
		areaLogo: String, // only used when roomType is hub
		BGcolor: {type: String, default: ''},
		inRoom: Boolean, // the current user is in the room
		viewingRoom: Boolean, // the current user is viewing the room - this is mutually exclusive with the previous state in normal circumstances
		conferenceType: String, // only used to determine what button should show when viewing
		online: Number, // number of users in room
		roomType: String, // loosely, the type of room - the only important ones are: hub, create_space
		usersInSearch: Array, // a list of users that are in the search + in this room
		userIsSearching: Boolean, // whether or not there is a string in search/query
		x: Number,
		y: Number,
		r: Number,
	},
	computed: {
		...mapGetters([
			'current_user/id',
			'viewport/isMobile'
		]),
		style: vm => ({
			'--x': vm.x+'px', '--y': vm.y+'px', '--r': vm.r,
			'--schemeBG': vm.BGcolor,
			'--schemeText': colorIsLight(vm.BGcolor) ? 'black' : 'white'
		}),
		buttonContent: vm => vm.isPortal ? buttonContentMap['portal'] : buttonContentMap[vm.conferenceType],
		isHub: vm => vm.roomType === 'hub',
		isCreate: vm => vm.roomType === 'create_space',
		isPortal: vm => vm.roomType === 'portal',
		logo: vm => processMediaURL(vm.areaLogo),
		areaInitials: vm => vm.areaTitle ? initaliseAreaName(vm.areaTitle) : '',
		usersToShow: vm => vm.userIsSearching ? vm.usersInSearch : (vm.inRoom ? [{userId: vm['current_user/id']}] : []),
		nameCapped(){
			let name = this.name;
			if(this['viewport/isMobile']) return name.length >= this.mobileNameLimit ? name.slice(0, this.mobileNameLimit)+'...' : name
			else return capRoomNamesToFit(name).replaceAll('<br>', '\n');
			// todo: can probably do better than capRoomNamesToFit
		}
	},
	methods: {
		getUserPosition(index){
			// offset is where the first user is positioned, in radians
			// uses a fixed offset if the current user is in the room, to ensure their icon is always in the same place
			// otherwise uses a random offset, for ~variety~, I guess
			// the random offset is only calculated once per room though, to keep things consistent within a room
			let offset = this.inRoom ? -0.9 : this.randomRadians;
			// by dividing TAU by the number of users, we can evenly space the users around the circle
			let radiansPerUser = TAU / this.usersToShow.length
			let radians = (index * radiansPerUser) + offset;
			// here we're finding the normalised Vector2 for a given angle in radians
			// then offsetting + scaling it by the radius of the circle
			// 50 is the "radius" because we're using % for positioning
			let x = 50+(Math.cos(radians)*50);
			let y = 50+(Math.sin(radians)*50);

			return {
				left: x+'%',
				top: y+'%'
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '~@/scss/components/space_overview/room.scss';
</style>;
